import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { directusOperations } from '../../lib/directus';
import { Visit, VisitsState, CreateVisitData } from '../../lib/types';
import { getWIBTimeForDirectus } from '../../lib/timeUtils';

// Async thunks for API calls
export const fetchVisitsLimited = createAsyncThunk(
  'visits/fetchVisitsLimited',
  async (limit = 10, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { visits: VisitsState };
      const now = Date.now();
      const lastFetched = state.visits.visitsLastFetched;
      const cacheTTL = state.visits.visitsCacheTTL;
      
      // Check cache for dashboard optimization
      if (lastFetched) {
        const lastFetchedTime = new Date(lastFetched).getTime();
        if (!isNaN(lastFetchedTime) && (now - lastFetchedTime) < cacheTTL) {
          // Return cached data by rejecting with special flag
          return rejectWithValue({ cached: true, message: 'Using cached visits data' });
        }
      }
      
      return await directusOperations.getVisits(limit);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch visits');
    }
  }
);

export const fetchVisits = createAsyncThunk(
  'visits/fetchVisits',
  async (limit = 50, { rejectWithValue }) => {
    try {
      return await directusOperations.getVisits(limit);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch visits');
    }
  }
);

export const fetchActiveVisit = createAsyncThunk(
  'visits/fetchActiveVisit',
  async (_, { rejectWithValue }) => {
    try {
      return await directusOperations.getActiveVisit();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch active visit');
    }
  }
);

export const createVisit = createAsyncThunk(
  'visits/createVisit',
  async (visitData: CreateVisitData, { rejectWithValue }) => {
    try {
      return await directusOperations.createVisit(visitData);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create visit');
    }
  }
);

export const updateVisit = createAsyncThunk(
  'visits/updateVisit',
  async ({ visitId, data }: { visitId: string; data: { notes?: string; satisfaction_rating?: number; revenue_potential?: number } }, { rejectWithValue }) => {
    try {
      return await directusOperations.updateVisit(visitId, data);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update visit');
    }
  }
);

export const endVisit = createAsyncThunk(
  'visits/endVisit',
  async (visitId: string, { rejectWithValue }) => {
    try {
      return await directusOperations.endVisit(visitId);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to end visit');
    }
  }
);

export const pauseVisit = createAsyncThunk(
  'visits/pauseVisit',
  async (visitId: string, { rejectWithValue }) => {
    try {
      return await directusOperations.pauseVisit(visitId);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to pause visit');
    }
  }
);

export const resumeVisit = createAsyncThunk(
  'visits/resumeVisit',
  async (visitId: string, { rejectWithValue }) => {
    try {
      return await directusOperations.resumeVisit(visitId);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to resume visit');
    }
  }
);

export const fetchVisitById = createAsyncThunk(
  'visits/fetchVisitById',
  async (visitId: string, { rejectWithValue }) => {
    try {
      return await directusOperations.getVisitById(visitId);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch visit details');
    }
  }
);

export const fetchTodaySchedule = createAsyncThunk(
  'visits/fetchTodaySchedule',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { visits: VisitsState };
      const now = Date.now();
      const lastFetched = state.visits.scheduleLastFetched;
      const cacheTTL = state.visits.scheduleCacheTTL;
      
      // Check cache for dashboard optimization (shorter TTL for schedule data)
      if (lastFetched) {
        const lastFetchedTime = new Date(lastFetched).getTime();
        if (!isNaN(lastFetchedTime) && (now - lastFetchedTime) < cacheTTL) {
          // Return cached data by rejecting with special flag
          return rejectWithValue({ cached: true, message: 'Using cached schedule data' });
        }
      }
      
      return await directusOperations.getTodaySchedule();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch today\'s schedule');
    }
  }
);

const initialState: VisitsState = {
  visits: [],
  activeVisit: null,
  todaySchedule: [],
  isLoading: false, // Deprecated - use granular loading states
  activeVisitLoading: false,
  historyLoading: false,
  actionLoading: false,
  scheduleLoading: false,
  error: null,
  lastUpdated: null,
  visitsLastFetched: null, // For TTL caching
  visitsCacheTTL: 30 * 60 * 1000, // 30 minutes in milliseconds
  scheduleLastFetched: null, // For schedule TTL caching
  scheduleCacheTTL: 5 * 60 * 1000, // 5 minutes in milliseconds (shorter for schedule)
};

const visitsSlice = createSlice({
  name: 'visits',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    
    setActiveVisit: (state, action: PayloadAction<Visit | null>) => {
      state.activeVisit = action.payload;
    },
    
    updateActiveVisitLocal: (state, action: PayloadAction<Partial<Visit>>) => {
      if (state.activeVisit) {
        Object.assign(state.activeVisit, action.payload);
      }
    },
    
    clearActiveVisit: (state) => {
      state.activeVisit = null;
    },
    
    // Local visit management for UI responsiveness
    addVisitToHistory: (state, action: PayloadAction<Visit>) => {
      const existingIndex = state.visits.findIndex(v => v.id === action.payload.id);
      if (existingIndex >= 0) {
        state.visits[existingIndex] = action.payload;
      } else {
        state.visits.unshift(action.payload);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch visits limited (for dashboard)
      .addCase(fetchVisitsLimited.pending, (state) => {
        state.historyLoading = true;
        state.error = null;
      })
      .addCase(fetchVisitsLimited.fulfilled, (state, action) => {
        state.historyLoading = false;
        state.visits = action.payload;
        state.visitsLastFetched = getWIBTimeForDirectus();
        state.lastUpdated = getWIBTimeForDirectus();
        state.error = null;
      })
      .addCase(fetchVisitsLimited.rejected, (state, action) => {
        state.historyLoading = false;
        // Handle cached data case
        const error = action.payload as any;
        if (error?.cached) {
          console.log('Using cached visits data');
        } else {
          state.error = error as string;
        }
      })
      
      // Fetch visits (full)
      .addCase(fetchVisits.pending, (state) => {
        state.historyLoading = true;
        state.error = null;
      })
      .addCase(fetchVisits.fulfilled, (state, action) => {
        state.historyLoading = false;
        state.visits = action.payload;
        state.visitsLastFetched = getWIBTimeForDirectus();
        state.lastUpdated = getWIBTimeForDirectus();
        state.error = null;
      })
      .addCase(fetchVisits.rejected, (state, action) => {
        state.historyLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch active visit
      .addCase(fetchActiveVisit.pending, (state) => {
        state.activeVisitLoading = true;
        state.error = null;
      })
      .addCase(fetchActiveVisit.fulfilled, (state, action) => {
        state.activeVisitLoading = false;
        state.activeVisit = action.payload;
        state.error = null;
      })
      .addCase(fetchActiveVisit.rejected, (state, action) => {
        state.activeVisitLoading = false;
        state.error = action.payload as string;
      })
      
      // Create visit
      .addCase(createVisit.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(createVisit.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.activeVisit = action.payload;
        state.visits.unshift(action.payload);
        state.error = null;
      })
      .addCase(createVisit.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      
      // Update visit
      .addCase(updateVisit.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(updateVisit.fulfilled, (state, action) => {
        state.actionLoading = false;
        
        // Update active visit if it matches
        if (state.activeVisit && state.activeVisit.id === action.payload.id) {
          state.activeVisit = action.payload;
        }
        
        // Update visit in history
        const index = state.visits.findIndex(v => v.id === action.payload.id);
        if (index >= 0) {
          state.visits[index] = action.payload;
        }
        
        state.error = null;
      })
      .addCase(updateVisit.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      
      // End visit
      .addCase(endVisit.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(endVisit.fulfilled, (state, action) => {
        state.actionLoading = false;
        
        // Update active visit and clear it
        if (state.activeVisit && state.activeVisit.id === action.payload.id) {
          state.activeVisit = null;
        }
        
        // Update visit in history
        const index = state.visits.findIndex(v => v.id === action.payload.id);
        if (index >= 0) {
          state.visits[index] = action.payload;
        } else {
          state.visits.unshift(action.payload);
        }
        
        state.error = null;
      })
      .addCase(endVisit.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      
      // Pause visit
      .addCase(pauseVisit.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(pauseVisit.fulfilled, (state, action) => {
        state.actionLoading = false;
        
        // Update active visit
        if (state.activeVisit && state.activeVisit.id === action.payload.id) {
          state.activeVisit = action.payload;
        }
        
        // Update visit in history
        const index = state.visits.findIndex(v => v.id === action.payload.id);
        if (index >= 0) {
          state.visits[index] = action.payload;
        }
        
        state.error = null;
      })
      .addCase(pauseVisit.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      
      // Resume visit
      .addCase(resumeVisit.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(resumeVisit.fulfilled, (state, action) => {
        state.actionLoading = false;
        
        // Update active visit
        if (state.activeVisit && state.activeVisit.id === action.payload.id) {
          state.activeVisit = action.payload;
        }
        
        // Update visit in history
        const index = state.visits.findIndex(v => v.id === action.payload.id);
        if (index >= 0) {
          state.visits[index] = action.payload;
        }
        
        state.error = null;
      })
      .addCase(resumeVisit.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch visit by ID
      .addCase(fetchVisitById.pending, (state) => {
        state.historyLoading = true;
        state.error = null;
      })
      .addCase(fetchVisitById.fulfilled, (state, action) => {
        state.historyLoading = false;
        
        // Update visit in history
        const index = state.visits.findIndex(v => v.id === action.payload.id);
        if (index >= 0) {
          state.visits[index] = { ...action.payload };
        } else {
          state.visits.unshift({ ...action.payload });
        }
        
        state.error = null;
      })
      .addCase(fetchVisitById.rejected, (state, action) => {
        state.historyLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch today's schedule
      .addCase(fetchTodaySchedule.pending, (state) => {
        state.scheduleLoading = true;
        state.error = null;
      })
      .addCase(fetchTodaySchedule.fulfilled, (state, action) => {
        state.scheduleLoading = false;
        state.todaySchedule = action.payload;
        state.scheduleLastFetched = getWIBTimeForDirectus();
        state.error = null;
      })
      .addCase(fetchTodaySchedule.rejected, (state, action) => {
        state.scheduleLoading = false;
        // Handle cached data case
        const error = action.payload as any;
        if (error?.cached) {
          console.log('Using cached schedule data');
        } else {
          state.error = error as string;
        }
      });
  },
});

export const {
  clearError,
  setActiveVisit,
  updateActiveVisitLocal,
  clearActiveVisit,
  addVisitToHistory,
} = visitsSlice.actions;

// Selectors
export const selectVisits = (state: { visits: VisitsState }) => state.visits;
export const selectVisitsList = (state: { visits: VisitsState }) => state.visits.visits;
export const selectActiveVisit = (state: { visits: VisitsState }) => state.visits.activeVisit;
export const selectVisitsLoading = (state: { visits: VisitsState }) => state.visits.isLoading; // Deprecated
export const selectVisitsError = (state: { visits: VisitsState }) => state.visits.error;
export const selectLastUpdated = (state: { visits: VisitsState }) => state.visits.lastUpdated;

// Granular loading selectors
export const selectActiveVisitLoading = (state: { visits: VisitsState }) => state.visits.activeVisitLoading;
export const selectHistoryLoading = (state: { visits: VisitsState }) => state.visits.historyLoading;
export const selectActionLoading = (state: { visits: VisitsState }) => state.visits.actionLoading;

// Combined loading selector for Dashboard
export const selectCriticalVisitDataLoading = (state: { visits: VisitsState }) => 
  state.visits.activeVisitLoading;

// Cache status selector
export const selectVisitsNeedRefresh = (state: { visits: VisitsState }) => {
  const { visitsLastFetched, visitsCacheTTL } = state.visits;
  if (!visitsLastFetched) return true;
  const now = Date.now();
  const lastFetched = new Date(visitsLastFetched).getTime();
  // Handle invalid dates
  if (isNaN(lastFetched)) return true;
  return (now - lastFetched) >= visitsCacheTTL;
};

// Helper selectors
export const selectCompletedVisits = (state: { visits: VisitsState }) => 
  state.visits.visits.filter(visit => visit.status === 'completed');

export const selectActiveVisits = (state: { visits: VisitsState }) => 
  state.visits.visits.filter(visit => visit.status === 'planned');

export const selectVisitById = (state: { visits: VisitsState }, visitId: string) => 
  state.visits.visits.find(visit => visit.id === visitId);

export const selectHasActiveVisit = (state: { visits: VisitsState }) => 
  !!state.visits.activeVisit;

// Dashboard-optimized selectors
export const selectTodayVisits = (state: { visits: VisitsState }) => {
  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return state.visits.visits.filter(visit => {
    const visitDate = new Date(visit.start_time);
    return visitDate >= todayStart;
  });
};

// Today's schedule selectors
export const selectTodaySchedule = (state: { visits: VisitsState }) => state.visits.todaySchedule;
export const selectScheduleLoading = (state: { visits: VisitsState }) => state.visits.scheduleLoading;
export const selectScheduleCount = (state: { visits: VisitsState }) => state.visits.todaySchedule.length;

// Cache status selector for schedule
export const selectScheduleNeedRefresh = (state: { visits: VisitsState }) => {
  const { scheduleLastFetched, scheduleCacheTTL } = state.visits;
  if (!scheduleLastFetched) return true;
  const now = Date.now();
  const lastFetched = new Date(scheduleLastFetched).getTime();
  // Handle invalid dates
  if (isNaN(lastFetched)) return true;
  return (now - lastFetched) >= scheduleCacheTTL;
};

export default visitsSlice.reducer;